import React from 'react';
import { Text } from 'rsuite';
import pluralize from 'pluralize';
import BasicStyledCheckbox from '../../../form/BasicStyledCheckbox';
import TaxReportingStatusIndicator from '../../TaxReportingStatusIndicator';
import { getAddressWithUnitNumber, titleize } from '../../../../common/utils';
import { PaymentPeriod, PeriodLicense } from '../../../../types/tax';
import IconIndicator from '../../../user-feedback/IconIndicator';
import { useResize } from '../../../../hooks/useResize';

interface TaxLicensesTableProps {
  licenses?: Array<PeriodLicense>;
  loading?: boolean;
  period: PaymentPeriod;
  permitType?: string;
  selectAll: (period: PaymentPeriod, isSelected: boolean) => void;
  selectLicense: (licenseNum: string, period: PaymentPeriod) => void;
  selectedLicenses?: Set<string>;
}

export const TaxLicensesTable = ({
  licenses = [],
  loading = false,
  period,
  permitType = '',
  selectAll,
  selectLicense,
  selectedLicenses = new Set<string>(),
}: TaxLicensesTableProps) => {
  const { isMobile } = useResize();

  if (loading) {
    return <IconIndicator />;
  }
  if (licenses?.length === 0) {
    return <Text>No {pluralize(permitType)} available for this period</Text>;
  }
  if (isMobile) {
    return (
      <div
        className="tax-license-cards"
        data-testid="tax-license-cards"
      >
        {licenses.map((license, index) => (
          <div
            key={`license_${index}`}
            className="tax-license-card"
            data-cy={`license-row-${license.license}`}
          >
            <div className="tax-license-card-address-row">
              <BasicStyledCheckbox
                label=""
                onChange={() => {
                  selectLicense(license.license ?? '', period);
                }}
                checked={selectedLicenses.has(license.license ?? '')}
              />
              <Text className="tax-license-card-address">
                {getAddressWithUnitNumber(license.address, license.unitNumber)}
              </Text>
            </div>
            <div className="tax-license-card-details">
              <div className="tax-license-card-detail-row">
                <Text className="tax-license-card-label">
                  {permitType} Number
                </Text>
                <Text className="tax-license-card-value">
                  {license.license}
                </Text>
              </div>
              <div className="tax-license-card-detail-row">
                <Text className="tax-license-card-label">
                  Tax Reporting This Period
                </Text>
                <span className="tax-status-badge">
                  <TaxReportingStatusIndicator status={license.totPaidStatus} />
                  {titleize(license.totPaidStatus ?? '')}
                </span>
              </div>
              <div className="tax-license-card-detail-row">
                <Text className="tax-license-card-label">
                  Owner&apos;s Name
                </Text>
                <Text className="tax-license-card-value">
                  {license.ownerName}
                </Text>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <table className="tax-table">
      <thead>
        <tr className="tax-column-heading-row">
          <th scope="col">
            <BasicStyledCheckbox
              label=""
              onChange={(checked: boolean) => {
                selectAll(period, checked);
              }}
            />
          </th>
          <th scope="col">Address</th>
          <th scope="col">{permitType} Number</th>
          <th scope="col">Tax Reporting This Period</th>
          <th scope="col">Owner&apos;s Name</th>
        </tr>
      </thead>
      <tbody className="tax-license-row-wrapper">
        {licenses.map((license, index) => (
          <tr
            key={`license_${index}`}
            className="tax-license-row"
            data-cy={`license-row-${license.license}`}
          >
            <td>
              <BasicStyledCheckbox
                label=""
                onChange={() => {
                  selectLicense(license.license ?? '', period);
                }}
                checked={selectedLicenses.has(license.license ?? '')}
              />
            </td>
            <td className="select-properties-address">
              {getAddressWithUnitNumber(license.address, license.unitNumber)}
            </td>
            <td>{license.license}</td>
            <td>
              <TaxReportingStatusIndicator status={license.totPaidStatus} />
              {titleize(license.totPaidStatus ?? '')}
            </td>
            <td>{license.ownerName}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
